import {
  LocalOnlineRoomService,
  type DeviceId,
  type LocalOnlineRoomClient,
  type ProfileId,
  type RoomId,
} from './online';

const PROTOCOL = 'CYRF1';
const CHANNEL_NAME = 'cybiko-native-rf-chat';
const MAX_LINE_CHARS = 320;
const MAX_MESSAGE_CHARS = 96;
const MAX_NAME_CHARS = 8;
const ONLINE_PACKET_PREFIX = 'CYRF1J ';
const ONLINE_TRANSPORT_POLL_INTERVAL_MS = 25;
const DEFAULT_RATE_LIMIT_MAX_MESSAGES = 8;
const DEFAULT_RATE_LIMIT_WINDOW_MS = 10_000;
const MESSAGE_BLOCKED_REASON = 'MESSAGE BLOCKED';
const RATE_LIMIT_REASON = 'SLOW DOWN';
const BASIC_PROFANITY_PATTERN = /\b(?:FUCK(?:ER|ING|ED)?|SHIT(?:TY)?|BITCH(?:ES)?|ASSHOLE(?:S)?|BADWORD)\b/i;

export const NATIVE_RF_CHAT_SURFACE = 'native-rf-chat-app-v1';
export const NATIVE_RF_CHAT_BROADCAST_TRANSPORT = 'broadcastchannel-rf-room-v1';
export const NATIVE_RF_CHAT_ONLINE_TRANSPORT = 'private-online-room-v1';
export const NATIVE_RF_CHAT_TRANSPORT = NATIVE_RF_CHAT_BROADCAST_TRANSPORT;
export const NATIVE_RF_CHAT_DEFAULT_ONLINE_ROOM_ID = 'native-rf-chat-local-room';
export const NATIVE_RF_CHAT_PUBLIC_ROOM_ID = 'public';

export type NativeRfChatRuntimeMode = 'disabled' | 'broadcast' | 'online';
export type NativeRfChatTransportLabel =
  | typeof NATIVE_RF_CHAT_BROADCAST_TRANSPORT
  | typeof NATIVE_RF_CHAT_ONLINE_TRANSPORT;
export type NativeRfChatConnectionState = 'connecting' | 'online' | 'offline' | 'error';

export type NativeRfChatParseResult =
  | { readonly kind: 'ignore' }
  | { readonly kind: 'hello'; readonly app: string }
  | { readonly kind: 'send'; readonly seq: number; readonly message: string }
  | { readonly kind: 'error'; readonly reason: string; readonly seq?: number };

export interface NativeRfChatWireMessage {
  readonly protocol: typeof PROTOCOL;
  readonly kind: 'presence' | 'chat' | 'leave';
  readonly peerId: string;
  readonly displayName: string;
  readonly seq: number;
  readonly message?: string;
  readonly sentAtMs: number;
}

export interface NativeRfChatTransportStatus {
  readonly state: NativeRfChatConnectionState;
  readonly reason?: string;
}

interface NativeRfChatClientStatusSource {
  onStatus(listener: (status: NativeRfChatTransportStatus) => void): () => void;
}

interface NativeRfChatClientEventSource {
  onEvent(listener: () => void): () => void;
}

export interface NativeRfChatTransport {
  readonly label?: NativeRfChatTransportLabel;
  send(message: NativeRfChatWireMessage): void;
  onMessage(listener: (message: NativeRfChatWireMessage) => void): () => void;
  onStatus?(listener: (status: NativeRfChatTransportStatus) => void): () => void;
  close(): void;
}

export interface NativeRfChatOnlineTransport extends NativeRfChatTransport {
  pump(): void;
}

export interface NativeRfChatOnlineTransportOptions {
  readonly pollIntervalMs?: number;
  readonly logWarning?: (message: string) => void;
}

export interface NativeRfChatLocalOnlineTransportOptions extends NativeRfChatOnlineTransportOptions {
  readonly service?: LocalOnlineRoomService;
  readonly roomId?: RoomId;
  readonly profileId?: ProfileId;
  readonly deviceId?: DeviceId;
  readonly displayName?: string;
}

export interface NativeRfChatRateLimitOptions {
  readonly maxMessages: number;
  readonly windowMs: number;
  readonly now?: () => number;
}

interface NativeRfChatNormalizedRateLimitOptions {
  readonly maxMessages: number;
  readonly windowMs: number;
  readonly now: () => number;
}

export interface NativeRfChatBridgeOptions {
  readonly peerId?: string;
  readonly displayName?: string;
  readonly transport?: NativeRfChatTransport;
  readonly sendConsoleText: (text: string) => void;
  readonly logWarning?: (message: string) => void;
  readonly moderateOutgoingText?: NativeRfChatTextModerationHook;
  readonly moderateIncomingText?: NativeRfChatTextModerationHook;
  readonly enableBasicProfanityFilter?: boolean;
  readonly sessionMutedPeerIds?: readonly string[];
  readonly sessionBlockedPeerIds?: readonly string[];
  readonly rateLimit?: NativeRfChatRateLimitOptions | false;
}

export interface NativeRfChatTextModerationContext {
  readonly direction: 'incoming' | 'outgoing';
  readonly peerId: string;
  readonly displayName: string;
  readonly seq: number;
}

export type NativeRfChatTextModerationHook = (
  message: string,
  context: NativeRfChatTextModerationContext,
) => string | undefined;

export interface NativeRfChatBridgeDebugState {
  readonly enabled: true;
  readonly protocol: typeof PROTOCOL;
  readonly surface: typeof NATIVE_RF_CHAT_SURFACE;
  readonly transport: NativeRfChatTransportLabel;
  readonly peerId: string;
  readonly displayName: string;
  readonly localAppName: string;
  readonly peersSeen: number;
  readonly receivedHellos: number;
  readonly sentMessages: number;
  readonly receivedMessages: number;
  readonly sentAcks: number;
  readonly sentErrors: number;
  readonly moderatedOutgoingMessages: number;
  readonly moderatedIncomingMessages: number;
  readonly rateLimitedMessages: number;
  readonly mutedPeerMessages: number;
  readonly blockedPeerMessages: number;
  readonly ignoredLines: number;
  readonly malformedLines: number;
  readonly lastSentSeq: number | null;
  readonly lastReceivedSeq: number | null;
  readonly lastPeerName: string;
  readonly lastError: string;
  readonly connectionState: NativeRfChatConnectionState;
}

export interface NativeRfChatBridge {
  receiveConsoleByte(byte: number): void;
  receiveConsoleText(text: string): void;
  debugState(): NativeRfChatBridgeDebugState;
  close(): void;
}

declare global {
  interface Window {
    __nativeRfChatBridgeDebug?: () => NativeRfChatBridgeDebugState | undefined;
    __nativeRfChatBridgeReceiveConsoleText?: (text: string) => void;
    __nativeRfChatOnlineMockService?: LocalOnlineRoomService;
  }
}

export function nativeRfChatRuntimeMode(search: string): NativeRfChatRuntimeMode {
  const value = new URLSearchParams(search).get('nativeRfChat');
  if (value === '0') return 'disabled';
  if (value === 'online' || value === 'public') return 'online';
  return 'broadcast';
}

export function shouldInitNativeRfChatBridge(search: string): boolean {
  return nativeRfChatRuntimeMode(search) !== 'disabled';
}

export function nativeRfChatOnlineRoomId(search: string): RoomId {
  const params = new URLSearchParams(search);
  if (params.get('nativeRfChat') === 'public') return NATIVE_RF_CHAT_PUBLIC_ROOM_ID;
  return cleanOnlineId(params.get('room') ?? '', NATIVE_RF_CHAT_DEFAULT_ONLINE_ROOM_ID);
}

export function parseNativeRfChatLine(line: string): NativeRfChatParseResult {
  const trimmed = line.replace(/[\r\n]+$/g, '').trim();
  if (!trimmed.startsWith(`${PROTOCOL} `)) return { kind: 'ignore' };

  const commandRead = readToken(trimmed.slice(PROTOCOL.length).trimStart());
  if (!commandRead) return { kind: 'error', reason: 'missing_command' };

  if (commandRead.token === 'HELLO') {
    return { kind: 'hello', app: normalizeRfName(commandRead.rest || 'RFCHAT') };
  }
  if (commandRead.token !== 'SEND') {
    return { kind: 'error', reason: 'unknown_command' };
  }

  const seqRead = readToken(commandRead.rest);
  if (!seqRead) return { kind: 'error', reason: 'missing_seq' };
  const seq = parseBoundedInt(seqRead.token, 0, 9999);
  if (seq === null) return { kind: 'error', reason: 'invalid_seq' };

  const lenRead = readToken(seqRead.rest);
  if (!lenRead) return { kind: 'error', reason: 'missing_length', seq };
  const declaredLength = parseBoundedInt(lenRead.token, 1, MAX_MESSAGE_CHARS);
  if (declaredLength === null) return { kind: 'error', reason: 'invalid_length', seq };

  const message = normalizeRfText(lenRead.rest);
  if (!message) return { kind: 'error', reason: 'empty_message', seq };
  if (message.length !== declaredLength) return { kind: 'error', reason: 'length_mismatch', seq };

  return { kind: 'send', seq, message };
}

export function serializeNativeRfChatReady(peerId: string): string {
  return `${PROTOCOL} READY ${safePeerToken(peerId)} LOCAL\r\n`;
}

export function serializeNativeRfChatReadyForState(
  peerId: string,
  state: NativeRfChatConnectionState,
): string {
  return `${PROTOCOL} READY ${safePeerToken(peerId)} ${stateReadyToken(state)}\r\n`;
}

export function serializeNativeRfChatPeer(displayName: string, state: 'ONLINE' | 'OFFLINE' | 'AWAY' = 'ONLINE'): string {
  return `${PROTOCOL} PEER ${normalizeRfName(displayName)} ${state}\r\n`;
}

export function serializeNativeRfChatSent(seq: number): string {
  return `${PROTOCOL} SENT ${boundedSeq(seq)}\r\n`;
}

export function serializeNativeRfChatMessage(displayName: string, message: string): string {
  const safeName = normalizeRfName(displayName);
  const safeMessage = normalizeRfText(message).slice(0, MAX_MESSAGE_CHARS) || '...';
  return `${PROTOCOL} MSG ${safeName} ${safeMessage.length} ${safeMessage}\r\n`;
}

export function serializeNativeRfChatError(seq: number, reason: string): string {
  const safeReason = normalizeRfText(reason).slice(0, 32) || 'ERROR';
  return `${PROTOCOL} ERR ${boundedSeq(seq)} ${safeReason.length} ${safeReason}\r\n`;
}

export function nativeRfChatBasicProfanityFilter(message: string): string | undefined {
  return BASIC_PROFANITY_PATTERN.test(normalizeRfText(message)) ? MESSAGE_BLOCKED_REASON : undefined;
}

export function createNativeRfChatBridge(options: NativeRfChatBridgeOptions): NativeRfChatBridge {
  const peerId = options.peerId ?? createPeerId();
  const displayName = normalizeRfName(options.displayName ?? peerId);
  const transport = options.transport ?? createBroadcastNativeRfChatTransport();
  const transportLabel = transport.label ?? NATIVE_RF_CHAT_BROADCAST_TRANSPORT;
  const peers = new Map<string, string>();
  const sessionMutedPeerIds = new Set(options.sessionMutedPeerIds ?? []);
  const sessionBlockedPeerIds = new Set(options.sessionBlockedPeerIds ?? []);
  const rateLimit = normalizeNativeRfChatRateLimit(options.rateLimit);
  const rateLimitWindow: number[] = [];
  let localAppName = '';
  let currentLine = '';
  let ready = false;
  let outgoingSeq = 0;
  let receivedHellos = 0;
  let sentMessages = 0;
  let receivedMessages = 0;
  let sentAcks = 0;
  let sentErrors = 0;
  let moderatedOutgoingMessages = 0;
  let moderatedIncomingMessages = 0;
  let rateLimitedMessages = 0;
  let mutedPeerMessages = 0;
  let blockedPeerMessages = 0;
  let ignoredLines = 0;
  let malformedLines = 0;
  let lastSentSeq: number | null = null;
  let lastReceivedSeq: number | null = null;
  let lastPeerName = '';
  let lastError = '';
  let connectionState: NativeRfChatConnectionState =
    transportLabel === NATIVE_RF_CHAT_ONLINE_TRANSPORT ? 'connecting' : 'online';

  const sendTransportMessage = (message: NativeRfChatWireMessage): boolean => {
    try {
      transport.send(message);
      return true;
    } catch (err) {
      const text = err instanceof Error ? err.message : String(err);
      lastError = 'transport_send_failed';
      options.logWarning?.(`native RF chat transport send failed: ${text}`);
      return false;
    }
  };

  const announcePresence = (): void => {
    if (!ready) return;
    sendTransportMessage({
      protocol: PROTOCOL,
      kind: 'presence',
      peerId,
      displayName,
      seq: outgoingSeq++,
      sentAtMs: Date.now(),
    });
  };

  const sendError = (seq: number, reason: string): void => {
    options.sendConsoleText(serializeNativeRfChatError(seq, reason));
    sentErrors += 1;
    lastError = reason;
  };

  const sendPeerStatus = (peerName: string, state: 'ONLINE' | 'OFFLINE' | 'AWAY'): void => {
    options.sendConsoleText(serializeNativeRfChatPeer(peerName, state));
  };

  const moderationReason = (
    message: string,
    context: NativeRfChatTextModerationContext,
    hook: NativeRfChatTextModerationHook | undefined,
  ): string | undefined => {
    const hookReason = hook?.(message, context);
    if (hookReason) return normalizeRfText(hookReason).slice(0, 32) || MESSAGE_BLOCKED_REASON;
    if (options.enableBasicProfanityFilter !== false) {
      return nativeRfChatBasicProfanityFilter(message);
    }
    return undefined;
  };

  const peerSuppressionReason = (remotePeerId: string): 'MUTED' | 'BLOCKED' | undefined => {
    if (sessionBlockedPeerIds.has(remotePeerId)) return 'BLOCKED';
    if (sessionMutedPeerIds.has(remotePeerId)) return 'MUTED';
    return undefined;
  };

  const consumeRateLimitSlot = (): boolean => {
    if (!rateLimit) return true;
    const now = rateLimit.now();
    while (rateLimitWindow.length > 0) {
      const oldest = rateLimitWindow[0];
      if (oldest === undefined || now - oldest < rateLimit.windowMs) break;
      rateLimitWindow.shift();
    }
    if (rateLimitWindow.length >= rateLimit.maxMessages) return false;
    rateLimitWindow.push(now);
    return true;
  };

  const detach = transport.onMessage((message) => {
    if (!isNativeRfChatWireMessage(message) || message.peerId === peerId) return;
    const peerName = normalizeRfName(message.displayName);
    const knownName = peers.get(message.peerId);
    peers.set(message.peerId, peerName);
    lastPeerName = peerName;

    if (message.kind === 'leave') {
      peers.set(message.peerId, peerName);
      if (ready) sendPeerStatus(peerName, 'OFFLINE');
      return;
    }

    if (ready && knownName !== peerName) {
      sendPeerStatus(peerName, 'ONLINE');
    }

    if (message.kind === 'presence') {
      return;
    }

    const safeMessage = normalizeRfText(message.message ?? '');
    if (!safeMessage) return;
    const peerSuppression = peerSuppressionReason(message.peerId);
    if (peerSuppression) {
      if (peerSuppression === 'MUTED') mutedPeerMessages += 1;
      else blockedPeerMessages += 1;
      lastReceivedSeq = message.seq;
      lastError = peerSuppression;
      return;
    }
    const moderationError = moderationReason(safeMessage, {
      direction: 'incoming',
      peerId: message.peerId,
      displayName: peerName,
      seq: message.seq,
    }, options.moderateIncomingText);
    if (moderationError) {
      moderatedIncomingMessages += 1;
      lastReceivedSeq = message.seq;
      lastError = moderationError;
      return;
    }
    receivedMessages += 1;
    lastReceivedSeq = message.seq;
    if (ready) options.sendConsoleText(serializeNativeRfChatMessage(peerName, safeMessage));
  });
  const detachStatus = transport.onStatus?.((status) => {
    connectionState = status.state;
    if (status.reason) lastError = normalizeRfText(status.reason);
    if (!ready) return;
    if (status.state === 'online') {
      options.sendConsoleText(serializeNativeRfChatReadyForState(peerId, status.state));
      return;
    }
    if (status.state === 'offline' && lastPeerName) {
      sendPeerStatus(lastPeerName, 'OFFLINE');
    }
    if (status.state === 'offline' || status.state === 'error') {
      sendError(0, status.reason ?? (status.state === 'offline' ? 'OFFLINE' : 'CONNECT FAILED'));
    }
  }) ?? (() => {});

  const processLine = (line: string): void => {
    const parsed = parseNativeRfChatLine(line);
    if (parsed.kind === 'ignore') {
      ignoredLines += 1;
      return;
    }
    if (parsed.kind === 'hello') {
      receivedHellos += 1;
      localAppName = parsed.app;
      ready = true;
      options.sendConsoleText(serializeNativeRfChatReadyForState(peerId, connectionState));
      if (connectionState === 'offline' || connectionState === 'error') {
        sendError(0, lastError || (connectionState === 'offline' ? 'OFFLINE' : 'CONNECT FAILED'));
      }
      announcePresence();
      for (const peerName of peers.values()) {
        sendPeerStatus(peerName, 'ONLINE');
      }
      return;
    }
    if (parsed.kind === 'error') {
      malformedLines += 1;
      lastError = parsed.reason;
      if (parsed.seq !== undefined) sendError(parsed.seq, parsed.reason);
      return;
    }
    if (!ready) {
      sendError(parsed.seq, 'NOT READY');
      return;
    }
    const moderationError = moderationReason(parsed.message, {
      direction: 'outgoing',
      peerId,
      displayName,
      seq: parsed.seq,
    }, options.moderateOutgoingText);
    if (moderationError) {
      moderatedOutgoingMessages += 1;
      sendError(parsed.seq, moderationError);
      return;
    }
    if (!consumeRateLimitSlot()) {
      rateLimitedMessages += 1;
      sendError(parsed.seq, RATE_LIMIT_REASON);
      return;
    }

    const sent = sendTransportMessage({
      protocol: PROTOCOL,
      kind: 'chat',
      peerId,
      displayName,
      seq: parsed.seq,
      message: parsed.message,
      sentAtMs: Date.now(),
    });
    if (!sent) {
      sendError(parsed.seq, 'SEND FAILED');
      return;
    }
    sentMessages += 1;
    sentAcks += 1;
    lastSentSeq = parsed.seq;
    lastError = '';
    options.sendConsoleText(serializeNativeRfChatSent(parsed.seq));
  };

  const flushLine = (): void => {
    const line = currentLine.trim();
    currentLine = '';
    if (line) processLine(line);
  };

  const receiveConsoleByte = (byte: number): void => {
    if (!Number.isInteger(byte)) return;
    const b = byte & 0xff;
    if (b === 0x0d || b === 0x0a) {
      flushLine();
      return;
    }
    if (b < 0x20 || b > 0x7e) return;
    currentLine += String.fromCharCode(b);
    if (currentLine.length > MAX_LINE_CHARS) {
      currentLine = '';
      malformedLines += 1;
      lastError = 'line_too_long';
    }
  };

  return {
    receiveConsoleByte,
    receiveConsoleText(text) {
      for (let index = 0; index < text.length; index += 1) {
        receiveConsoleByte(text.charCodeAt(index));
      }
    },
    debugState() {
      return {
        enabled: true,
        protocol: PROTOCOL,
        surface: NATIVE_RF_CHAT_SURFACE,
        transport: transportLabel,
        peerId,
        displayName,
        localAppName,
        peersSeen: peers.size,
        receivedHellos,
        sentMessages,
        receivedMessages,
        sentAcks,
        sentErrors,
        moderatedOutgoingMessages,
        moderatedIncomingMessages,
        rateLimitedMessages,
        mutedPeerMessages,
        blockedPeerMessages,
        ignoredLines,
        malformedLines,
        lastSentSeq,
        lastReceivedSeq,
        lastPeerName,
        lastError,
        connectionState,
      };
    },
    close() {
      detach();
      detachStatus();
      transport.close();
    },
  };
}

export function createBroadcastNativeRfChatTransport(
  channelName = CHANNEL_NAME,
): NativeRfChatTransport {
  if (typeof BroadcastChannel === 'undefined') {
    return {
      label: NATIVE_RF_CHAT_BROADCAST_TRANSPORT,
      send() {},
      onMessage() {
        return () => {};
      },
      onStatus(listener) {
        listener({ state: 'offline', reason: 'BROADCAST UNAVAILABLE' });
        return () => {};
      },
      close() {},
    };
  }

  const channel = new BroadcastChannel(channelName);
  return {
    label: NATIVE_RF_CHAT_BROADCAST_TRANSPORT,
    send(message) {
      channel.postMessage(message);
    },
    onMessage(listener) {
      const handler = (event: MessageEvent<NativeRfChatWireMessage>): void => {
        listener(event.data);
      };
      channel.addEventListener('message', handler);
      return () => channel.removeEventListener('message', handler);
    },
    onStatus(listener) {
      listener({ state: 'online' });
      return () => {};
    },
    close() {
      channel.close();
    },
  };
}

export function createOnlineNativeRfChatTransport(
  client: LocalOnlineRoomClient,
  options: NativeRfChatOnlineTransportOptions = {},
): NativeRfChatOnlineTransport {
  const listeners = new Set<(message: NativeRfChatWireMessage) => void>();
  const statusListeners = new Set<(status: NativeRfChatTransportStatus) => void>();
  const presenceNames = new Map<ProfileId, string>();
  const pollIntervalMs = options.pollIntervalMs ?? ONLINE_TRANSPORT_POLL_INTERVAL_MS;
  if (!Number.isFinite(pollIntervalMs) || pollIntervalMs < 0) {
    throw new Error('native RF chat online transport poll interval must be non-negative');
  }
  let closed = false;
  let pollTimer: ReturnType<typeof setInterval> | undefined;
  let status: NativeRfChatTransportStatus = { state: 'online' };
  let detachClientEvents = (): void => {};
  let eventPumpStarted = false;

  const pump = (): void => {
    if (closed) return;
    for (const event of client.drainEvents()) {
      if (event.type === 'presence') {
        const peerName = normalizeRfName(event.presence.displayName);
        presenceNames.set(event.presence.profileId, peerName);
        emitMessage({
          protocol: PROTOCOL,
          kind: 'presence',
          peerId: event.presence.profileId,
          displayName: peerName,
          seq: 0,
          sentAtMs: event.presence.lastSeenAtMs,
        });
        continue;
      }
      if (event.type === 'presence-left') {
        emitMessage({
          protocol: PROTOCOL,
          kind: 'leave',
          peerId: event.profileId,
          displayName: presenceNames.get(event.profileId) ?? 'PEER',
          seq: 0,
          sentAtMs: event.leftAtMs,
        });
        continue;
      }
      if (event.type !== 'rf-packet') continue;
      const message = decodeOnlineWireMessage(event.packet.bytes);
      if (!message) continue;
      emitMessage(message);
    }
  };

  const emitMessage = (message: NativeRfChatWireMessage): void => {
    for (const listener of listeners) listener(message);
  };

  const emitStatus = (next: NativeRfChatTransportStatus): void => {
    status = next;
    for (const listener of statusListeners) listener(next);
  };

  const detachClientStatus = nativeRfChatClientStatusSource(client)?.onStatus((next) => {
    emitStatus(next);
  }) ?? (() => {});

  const startEventPump = (): void => {
    if (eventPumpStarted) return;
    eventPumpStarted = true;
    detachClientEvents = nativeRfChatClientEventSource(client)?.onEvent(() => {
      pump();
    }) ?? (() => {});
    if (pollIntervalMs > 0) {
      pollTimer = setInterval(pump, pollIntervalMs);
    }
  };

  return {
    label: NATIVE_RF_CHAT_ONLINE_TRANSPORT,
    pump,
    send(message) {
      if (closed) return;
      client.sendPacket(encodeOnlineWireMessage(message));
    },
    onMessage(listener) {
      listeners.add(listener);
      startEventPump();
      pump();
      return () => listeners.delete(listener);
    },
    onStatus(listener) {
      statusListeners.add(listener);
      listener(status);
      return () => statusListeners.delete(listener);
    },
    close() {
      closed = true;
      detachClientStatus();
      detachClientEvents();
      emitStatus({ state: 'offline', reason: 'OFFLINE' });
      if (pollTimer !== undefined) {
        clearInterval(pollTimer);
        pollTimer = undefined;
      }
      listeners.clear();
      statusListeners.clear();
      client.disconnect();
    },
  };
}

export function createUnavailableNativeRfChatTransport(
  reason = 'CONNECT FAILED',
): NativeRfChatTransport {
  const safeReason = normalizeRfText(reason).slice(0, 32) || 'CONNECT FAILED';
  return {
    label: NATIVE_RF_CHAT_ONLINE_TRANSPORT,
    send() {
      throw new Error(safeReason);
    },
    onMessage() {
      return () => {};
    },
    onStatus(listener) {
      listener({ state: 'error', reason: safeReason });
      return () => {};
    },
    close() {},
  };
}

export function createLocalOnlineNativeRfChatTransport(
  options: NativeRfChatLocalOnlineTransportOptions = {},
): NativeRfChatOnlineTransport {
  const service = options.service ?? browserNativeRfChatOnlineService();
  const displayName = normalizeRfName(options.displayName ?? 'CYBIKO');
  const suffix = Math.random().toString(36).slice(2, 10);
  const profileId = options.profileId ?? `rfchat-${displayName.toLowerCase()}-${suffix}`;
  const deviceId = options.deviceId ?? `${profileId}-device`;
  service.upsertProfile({
    id: profileId,
    handle: profileId,
    displayName,
  });
  service.upsertDevice({
    id: deviceId,
    profileId,
    label: 'Native RF Chat',
  });
  const client = service.joinRoom({
    roomId: options.roomId ?? NATIVE_RF_CHAT_DEFAULT_ONLINE_ROOM_ID,
    profileId,
    deviceId,
  });
  return createOnlineNativeRfChatTransport(client, options);
}

function isNativeRfChatWireMessage(value: unknown): value is NativeRfChatWireMessage {
  if (!value || typeof value !== 'object') return false;
  const record = value as Partial<NativeRfChatWireMessage>;
  return (
    record.protocol === PROTOCOL &&
    (record.kind === 'presence' || record.kind === 'chat' || record.kind === 'leave') &&
    typeof record.peerId === 'string' &&
    typeof record.displayName === 'string' &&
    Number.isInteger(record.seq) &&
    Number.isFinite(record.sentAtMs)
  );
}

function encodeOnlineWireMessage(message: NativeRfChatWireMessage): readonly number[] {
  const text = `${ONLINE_PACKET_PREFIX}${JSON.stringify(message)}`;
  return [...new TextEncoder().encode(text)];
}

function decodeOnlineWireMessage(bytes: readonly number[]): NativeRfChatWireMessage | null {
  try {
    const text = new TextDecoder().decode(Uint8Array.from(bytes));
    if (!text.startsWith(ONLINE_PACKET_PREFIX)) return null;
    const value: unknown = JSON.parse(text.slice(ONLINE_PACKET_PREFIX.length));
    return isNativeRfChatWireMessage(value) ? value : null;
  } catch {
    return null;
  }
}

function readToken(value: string): { readonly token: string; readonly rest: string } | null {
  const trimmed = value.trimStart();
  if (!trimmed) return null;
  const idx = trimmed.search(/\s/);
  if (idx < 0) return { token: trimmed, rest: '' };
  return { token: trimmed.slice(0, idx), rest: trimmed.slice(idx + 1) };
}

function parseBoundedInt(value: string, min: number, max: number): number | null {
  if (!/^\d+$/.test(value)) return null;
  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed) || parsed < min || parsed > max) return null;
  return parsed;
}

function boundedSeq(seq: number): number {
  const parsed = parseBoundedInt(String(seq), 0, 9999);
  if (parsed === null) throw new Error('invalid Native RF Chat sequence');
  return parsed;
}

export function normalizeRfText(text: string): string {
  return text
    .toUpperCase()
    .replace(/[^ A-Z0-9.,:;?!'<>/\\=\-_*]/g, '?')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_MESSAGE_CHARS);
}

export function normalizeRfName(text: string): string {
  const name = text
    .toUpperCase()
    .replace(/[^A-Z0-9_-]/g, '')
    .slice(0, MAX_NAME_CHARS);
  return name || 'CYBIKO';
}

function stateReadyToken(state: NativeRfChatConnectionState): 'LOCAL' | 'ONLINE' | 'OFFLINE' | 'ERROR' {
  if (state === 'online') return 'ONLINE';
  if (state === 'offline') return 'OFFLINE';
  if (state === 'error') return 'ERROR';
  return 'LOCAL';
}

function safePeerToken(peerId: string): string {
  const token = peerId
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, MAX_NAME_CHARS);
  return token || 'PEER';
}

function cleanOnlineId(value: string, fallback: string): string {
  const clean = value
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48);
  return clean || fallback;
}

function normalizeNativeRfChatRateLimit(
  value: NativeRfChatRateLimitOptions | false | undefined,
): NativeRfChatNormalizedRateLimitOptions | undefined {
  if (value === false) return undefined;
  const maxMessages = clampInt(
    value?.maxMessages ?? DEFAULT_RATE_LIMIT_MAX_MESSAGES,
    1,
    60,
  );
  const windowMs = clampInt(
    value?.windowMs ?? DEFAULT_RATE_LIMIT_WINDOW_MS,
    1_000,
    60_000,
  );
  return {
    maxMessages,
    windowMs,
    now: value?.now ?? Date.now,
  };
}

function clampInt(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, Math.round(value)));
}

function browserNativeRfChatOnlineService(): LocalOnlineRoomService {
  if (typeof window === 'undefined') return new LocalOnlineRoomService();
  window.__nativeRfChatOnlineMockService ??= new LocalOnlineRoomService();
  return window.__nativeRfChatOnlineMockService;
}

function nativeRfChatClientStatusSource(
  client: LocalOnlineRoomClient,
): NativeRfChatClientStatusSource | undefined {
  const candidate = client as Partial<NativeRfChatClientStatusSource>;
  return typeof candidate.onStatus === 'function' ? candidate as NativeRfChatClientStatusSource : undefined;
}

function nativeRfChatClientEventSource(
  client: LocalOnlineRoomClient,
): NativeRfChatClientEventSource | undefined {
  const candidate = client as Partial<NativeRfChatClientEventSource>;
  return typeof candidate.onEvent === 'function' ? candidate as NativeRfChatClientEventSource : undefined;
}

function createPeerId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `cy${crypto.randomUUID().replace(/-/g, '').slice(0, 6)}`;
  }
  return `cy${Math.random().toString(36).slice(2, 8)}`;
}
